"use client";

import React, { useState, useEffect } from "react";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  VariableProductAddToCart,
  ProductPrice,
  type EcommerceProduct,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPQueryParams: () => Record<string, any>;
    };
  }
}

function useWvcLanguage(): string {
  const [lang, setLang] = useState<string>("en");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateLang = () => {
      const htmlLang = document.documentElement.lang?.slice(0, 2).toLowerCase();
      const path = window.location.pathname;
      const match = path.match(/^\/([a-z]{2})(?:-[a-z]{2})?(?:\/|$)/i);
      const detected = match ? match[1].toLowerCase() : htmlLang || "en";
      setLang(detected);
    };

    updateLang();
    window.addEventListener("popstate", updateLang);
    return () => window.removeEventListener("popstate", updateLang);
  }, []);

  return ["en", "zh", "fr", "es", "de"].includes(lang) ? lang : "en";
}

const translations: Record<
  string,
  {
    loadError: string;
    tryAgain: string;
    notFound: string;
    returnToShop: string;
    detailsAndCare: string;
    categories: string;
    tags: string;
    youMayAlsoLike: string;
    curatedCompanions: string;
    viewAll: string;
    sale: string;
  }
> = {
  en: {
    loadError: "Unable to display this product at the moment.",
    tryAgain: "Try again",
    notFound: "Product not found.",
    returnToShop: "Return to shop",
    detailsAndCare: "Details and care",
    categories: "Categories:",
    tags: "Tags:",
    youMayAlsoLike: "You may also like",
    curatedCompanions: "Curated companions",
    viewAll: "View all",
    sale: "Sale",
  },
  zh: {
    loadError: "目前无法显示此产品。",
    tryAgain: "再试一次",
    notFound: "产品未找到。",
    returnToShop: "返回商店",
    detailsAndCare: "细节和护理",
    categories: "类别:",
    tags: "标签:",
    youMayAlsoLike: "您可能也喜欢",
    curatedCompanions: "策划伴侣",
    viewAll: "查看所有",
    sale: "销售",
  },
  fr: {
    loadError: "Impossible d'afficher ce produit en ce moment.",
    tryAgain: "Réessayer",
    notFound: "Produit non trouvé.",
    returnToShop: "Retour à la Boutique",
    detailsAndCare: "Détails et Soins",
    categories: "Catégories:",
    tags: "Étiquettes:",
    youMayAlsoLike: "Vous Aimerez Aussi",
    curatedCompanions: "Compagnons Curés",
    viewAll: "Afficher tout",
    sale: "Solde",
  },
  es: {
    loadError: "No se puede mostrar este producto en este momento.",
    tryAgain: "Intentar de Nuevo",
    notFound: "Producto no encontrado.",
    returnToShop: "Volver a la Tienda",
    detailsAndCare: "Detalles y Cuidado",
    categories: "Categorías:",
    tags: "Etiquetas:",
    youMayAlsoLike: "También te podría gustar",
    curatedCompanions: "Compañeros Curados",
    viewAll: "Ver todo",
    sale: "Venta",
  },
  de: {
    loadError: "Dieses Produkt kann derzeit nicht angezeigt werden.",
    tryAgain: "Erneut Versuchen",
    notFound: "Produkt nicht gefunden.",
    returnToShop: "Zurück zum Shop",
    detailsAndCare: "Details und Pflege",
    categories: "Kategorien:",
    tags: "Tags:",
    youMayAlsoLike: "Sie mögen auch",
    curatedCompanions: "Kuratierte Begleiter",
    viewAll: "Alles anzeigen",
    sale: "Verkauf",
  },
};

function ProductDetailConsumer() {
  const language = useWvcLanguage();
  const t = translations[language] || translations.en;
  const { products, loading, error, refetch } = useEcommerceProducts();
  const [activeImage, setActiveImage] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7 space-y-4">
            <div className="aspect-3/4 w-full animate-pulse bg-muted rounded-none" />
            <div className="flex gap-3">
              {[0, 1, 2].map((_, i) => (
                <div
                  key={i}
                  data-index={i}
                  className="size-20 animate-pulse bg-muted rounded-none"
                />
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 space-y-6">
            <div className="h-8 w-3/4 animate-pulse bg-muted rounded-none" />
            <div className="h-6 w-1/4 animate-pulse bg-muted rounded-none" />
            <div className="h-20 w-full animate-pulse bg-muted rounded-none" />
            <div className="h-12 w-full animate-pulse bg-muted rounded-none" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-sm text-muted-foreground mb-4">
          {t.loadError}
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          {t.tryAgain}
        </Button>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-base text-muted-foreground">{t.notFound}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/ShopPage">{t.returnToShop}</Link>
        </Button>
      </div>
    );
  }

  const product: EcommerceProduct = products[0];
  const galleryImages =
    product.gallery_image_urls && product.gallery_image_urls.length > 0
      ? product.gallery_image_urls
      : product.featured_image_url
        ? [product.featured_image_url]
        : [];

  const mainImage = activeImage ?? galleryImages[0] ?? "";
  const relatedProducts = products.slice(1, 5);

  return (
    <article className="container mx-auto px-4 py-10 md:py-16 lg:py-20">
      {/* Main product area */}
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16 items-start">
        {/* Gallery column */}
        <div className="lg:col-span-7">
          <div className="flex flex-col-reverse gap-4 md:flex-row">
            {galleryImages.length > 1 && (
              <div
                className="flex md:flex-col gap-3 overflow-x-auto md:overflow-y-auto md:max-h-[620px] scrollbar-none shrink-0"
                role="group"
                aria-label="Product thumbnails"
              >
                {galleryImages.map((imgUrl, i) => {
                  const isCurrent = imgUrl === mainImage;
                  return (
                    <Button
                      key={imgUrl + i}
                      data-index={i}
                      type="button"
                      variant="ghost"
                      onClick={() => setActiveImage(imgUrl)}
                      aria-label={`${product.title} view ${i + 1}`}
                      aria-current={isCurrent ? "true" : undefined}
                      className={`relative size-18 md:size-20 p-0 overflow-hidden rounded-none border transition-all ${
                        isCurrent
                          ? "border-primary ring-1 ring-primary"
                          : "border-border opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={imgUrl}
                        alt=""
                        aria-hidden="true"
                        className="h-full w-full object-cover object-center"
                        loading="lazy"
                        decoding="async"
                      />
                    </Button>
                  );
                })}
              </div>
            )}

            <div className="relative flex-1 aspect-3/4 overflow-hidden bg-muted border border-border">
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={product.title}
                  className="h-full w-full object-cover object-center"
                  decoding="async"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                  No image available
                </div>
              )}
              {product.on_sale && (
                <div className="absolute top-4 left-4">
                  <Badge variant="default" className="uppercase tracking-widest text-[11px] px-2.5 py-1">
                    {t.sale}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Product information column */}
        <div className="lg:col-span-5 flex flex-col justify-start space-y-6">
          <header className="space-y-3">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground">
              {product.title}
            </h1>
            <div className="pt-1">
              <ProductPrice
                product={product}
                as="div"
                className="text-xl sm:text-2xl font-medium text-foreground tracking-tight"
              />
            </div>
          </header>

          <Separator className="bg-border/60" />

          {/* Short description */}
          {product.short_description && (
            <div
              className="text-sm leading-relaxed text-muted-foreground font-sans [&_p]:mb-3 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          {/* Add to cart section with attributes / variation picker */}
          <div className="pt-2">
            <VariableProductAddToCart
              product={product}
              onVariationResolved={(v) =>
                setActiveImage(v?.featured_image_url ?? null)
              }
              className="w-full space-y-5"
              buttonClassName="bg-primary text-foreground-primary w-full h-12 uppercase tracking-wider text-xs font-semibold rounded-none hover:bg-primary/90"
            />
          </div>

          <Separator className="bg-border/60" />

          {/* Main detailed description */}
          {product.long_description && (
            <section className="space-y-2 pt-2">
              <h2 className="text-xs uppercase tracking-widest font-semibold text-foreground">
                {t.detailsAndCare}
              </h2>
              <div
                className="text-sm leading-relaxed text-muted-foreground font-sans space-y-2 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
                dangerouslySetInnerHTML={{ __html: product.long_description }}
              />
            </section>
          )}

          {/* Product Meta */}
          {((product.categories && product.categories.length > 0) ||
            (product.tags && product.tags.length > 0)) && (
            <footer className="pt-4 space-y-3 text-xs text-muted-foreground border-t border-border/60">
              {product.categories && product.categories.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-foreground">{t.categories}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {product.categories.map((cat, i) => (
                      <span key={cat.id || i} data-index={i}>
                        <Link
                          to={cat.link || "/ShopPage"}
                          className="hover:text-foreground underline-offset-2 hover:underline"
                        >
                          {cat.name}
                        </Link>
                        {i < product.categories.length - 1 && ","}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-foreground">{t.tags}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {product.tags.map((tag, i) => (
                      <span key={tag.id || i} data-index={i}>
                        <Link
                          to={tag.link || "/ShopPage"}
                          className="hover:text-foreground underline-offset-2 hover:underline"
                        >
                          {tag.name}
                        </Link>
                        {i < product.tags.length - 1 && ","}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </footer>
          )}
        </div>
      </div>

      {/* Recommendations / Related Products */}
      {relatedProducts.length > 0 && (
        <section className="mt-20 lg:mt-28 border-t border-border pt-12 md:pt-16">
          <div className="flex items-baseline justify-between mb-8 md:mb-10">
            <div>
              <h2 className="font-serif text-2xl md:text-3xl text-foreground font-normal">
                {t.youMayAlsoLike}
              </h2>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                {t.curatedCompanions}
              </p>
            </div>
            <Button asChild variant="link" className="p-0 text-xs uppercase tracking-wider text-foreground">
              <Link to="/ShopPage">{t.viewAll}</Link>
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {relatedProducts.map((relProduct, i) => (
              <div key={relProduct.id} data-index={i} className="group flex flex-col">
                <Link
                  to={relProduct.link || `/ShopPage`}
                  className="block aspect-3/4 overflow-hidden bg-muted border border-border relative mb-3"
                >
                  {relProduct.featured_image_url ? (
                    <img
                      src={relProduct.featured_image_url}
                      alt={relProduct.title}
                      className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                      No image
                    </div>
                  )}
                  {relProduct.on_sale && (
                    <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] uppercase font-semibold px-2 py-0.5">
                      {t.sale}
                    </span>
                  )}
                </Link>

                <div className="flex flex-col flex-1">
                  <h3 className="text-sm font-medium text-foreground tracking-normal truncate mb-1">
                    <Link to={relProduct.link || `/ShopPage`} className="hover:underline">
                      {relProduct.title}
                    </Link>
                  </h3>
                  <ProductPrice
                    product={relProduct}
                    as="div"
                    className="text-xs font-semibold text-foreground/90"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

export default function ProductDetail() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  return (
    <section
      id="single-product-template-section"
      data-nav="light"
      className="bg-background text-foreground relative w-full"
    >
      <EcommerceProductsProvider wp_query={currentWPQueryParams}>
        <ProductDetailConsumer />
      </EcommerceProductsProvider>
    </section>
  );
}